import { api } from './api'
import { quoteItemsApi } from './quoteItems'

const apiVersion = 'v2'

export const quoteItemAttachmentsPath = ({ organisationId, quoteId, quoteItemId }) =>
    `/${apiVersion}/organisations/${organisationId}/quotes/${quoteId}/quote-items/${quoteItemId}/attachments`

// Writes refetch only this item's list. The quote items are left alone: reloading every item
// for one attachment change is what the list endpoint exists to avoid.
const invalidateItemList = (_result, _error, { quoteItemId }) => [{ type: 'QuoteItemAttachments', id: quoteItemId }]

export const quoteItemAttachmentsApi = api
    .enhanceEndpoints({
        addTagTypes: ['QuoteItemAttachments'],
    })
    .injectEndpoints({
        endpoints: (builder) => ({
            getQuoteItemAttachments: builder.query({
                query: quoteItemAttachmentsPath,
                providesTags: (_result, _error, { quoteItemId }) => [{ type: 'QuoteItemAttachments', id: quoteItemId }],
                // The item row works out its count and failed marker from the attachments that
                // arrive with the quote items. Copy the rows from every list fetch (on open and
                // after a write) so the row stays current without refetching the quote items.
                async onQueryStarted({ organisationId, quoteId, quoteItemId }, { dispatch, queryFulfilled }) {
                    try {
                        const { data: rows } = await queryFulfilled
                        dispatch(
                            quoteItemsApi.util.updateQueryData(
                                'getQuoteItems',
                                { organisationId, quoteId },
                                (draft) => {
                                    const item = draft?.find(({ id }) => id === quoteItemId)
                                    if (!item) return
                                    item.attachments = rows
                                }
                            )
                        )
                    } catch {
                        // The failed query already reports its own error.
                    }
                },
            }),

            // Fetched at click time: the link is a short-lived SAS URI, so it is never cached.
            getQuoteItemAttachmentDownloadUrl: builder.query({
                query: ({ documentId, ...parent }) => `${quoteItemAttachmentsPath(parent)}/${documentId}/download-url`,
                keepUnusedDataFor: 0,
            }),

            setQuoteItemAttachmentVisibility: builder.mutation({
                query: ({ documentId, isPublic, ...parent }) => ({
                    url: `${quoteItemAttachmentsPath(parent)}/${documentId}/visibility`,
                    method: 'PATCH',
                    body: { isPublic },
                }),
                invalidatesTags: invalidateItemList,
            }),

            deleteQuoteItemAttachment: builder.mutation({
                query: ({ documentId, ...parent }) => ({
                    url: `${quoteItemAttachmentsPath(parent)}/${documentId}`,
                    method: 'DELETE',
                }),
                invalidatesTags: invalidateItemList,
            }),
        }),
    })

export const {
    useDeleteQuoteItemAttachmentMutation,
    useGetQuoteItemAttachmentsQuery,
    useLazyGetQuoteItemAttachmentDownloadUrlQuery,
    useSetQuoteItemAttachmentVisibilityMutation,
} = quoteItemAttachmentsApi
